import React, { useState, useEffect } from 'react';
import useFilters from 'utils/hooks/useFilters';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import CustomTable from 'components/CustomTable/CustomTable';
import useCountries from 'utils/hooks/fetchHooks/useCountries';
import { notification, Modal, theme, Button } from 'antd';
import { branchOp } from 'store/ducks/DataEntry/Branch';
import { IApiBranch, IBranch } from 'store/ducks/DataEntry/Branch/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getDefaultColumns } from 'components/Columns/BranchTableColumns';
import SaveButton from 'components/SaveButton/SaveButton';
import { requiredFieldsForBranch } from 'constants/requiredFields';
import { globalOp } from 'store/ducks/Global';
import { useTranslation } from 'react-i18next';
import * as Styled from '../../DataEntry.styled';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const Branch: React.FC = () => {
  i18nIsoCountries.registerLocale(enLocale);
  const { query } = useFilters();
  const { confirm } = Modal;
  const { useToken } = theme;
  const { token } = useToken();
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState<IBranch[]>([]);
  const { countries } = useCountries();
  const [count, setCount] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const isLoggedIn = localStorage.getItem('authToken');

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleDateChange = (key: number, field: string, dateString: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        [field]: dateString,
        isEdited: !item.isNew,
      });
      setDataSource(newData);
    }
  };

  const fetchBranchbyEntity = async () => {
    try {
      const response = await branchOp.fetchBranchbyEntityName({
        entity_id: query.entityId,
        entity_name: query.entity,
      });

      const data = response?.data;

      const mappedData: IBranch[] = (data?.branches || []).map(
        (branch: IApiBranch, index: number): IBranch => ({
          key: index,
          id_branch: branch.id,
          head_office_lei: branch.head_office_lei,
          branch_name: branch.branch_name,
          branch_identification_code: branch.branch_identification_code,
          description_branch: branch.description,
          entity_id: branch.entity_id,
          id_country: branch.country?.id ?? '',
          country_name: branch.country?.name ?? '',
          iso_code: branch.country?.iso_code ?? '',
          country_description: branch.country?.description ?? '',
          start_date: branch.start_date,
          end_date: branch.end_date,
          isNew: false,
          isEdited: false,
        }),
      );

      setDataSource(mappedData);
      setCount(mappedData.length);
    } catch (error) {
      setDataSource([]);
      console.log(error, 'error');
    }
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = dataSource.filter((item) => item.key !== key);
        setDataSource(updatedDataSource);
        fetchBranchbyEntity();
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 10,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 10,
        });
      },
    });
  };

  const handleInactivate = (record: IBranch) => {
    if (!record.id_branch) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: record.id_branch,
      tableName: 'branch',
      onSuccess: () => {
        fetchBranchbyEntity();
      },
    });
  };

  useEffect(() => {
    if (query.entity) {
      fetchBranchbyEntity();
    }
  }, [query]);

  const handleAddRow = (record?: any, index?: number) => {
    const newData: IBranch = {
      key: generateUniqueId(),
      id_branch: generateUniqueId(),
      head_office_lei: '',
      branch_name: '',
      branch_identification_code: '',
      start_date: '1900-01-01',
      end_date: '9999-01-01',
      description_branch: '',
      entity_id: query.entityId,
      country_description: '',
      id_country: '',
      country_name: '',
      iso_code: '',
      isNew: true,
    };
    const insertIndex = dataSource.findIndex(
      (item) => item.key === record?.key,
    );

    const updatedDataSource = [
      ...dataSource.slice(0, insertIndex + 1),
      newData,
      ...dataSource.slice(insertIndex + 1),
    ];

    setDataSource(updatedDataSource);
    setCount(count + 1);
  };

  const handleSave = (row: IBranch) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    newData.splice(index, 1, {
      ...newData[index],
      ...row,
      isEdited: !newData[index].isNew,
    });
    setDataSource(newData);
  };

  const handleSubmit = async () => {
    const changedData = dataSource.filter(
      (item) => item.isNew || item.isEdited,
    );

    if (changedData.length === 0) {
      notification.info({
        message: 'Info',
        description: 'There are no new or edited Branches to submit.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const hasEmptyFields = changedData.some((item) =>
      (requiredFieldsForBranch as (keyof typeof item)[]).some(
        (field) => item[field] === '' || item[field] === null,
      ),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please complete all the fields highlighted in yellow and make sure to enter a start date before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const formattedData = {
      entity_name: query.entity,
      data_list: [
        {
          Branch: changedData.map((item) => ({
            id: item.id_branch,
            head_office_lei: item.head_office_lei,
            branch_name: item.branch_name,
            branch_identification_code: item.branch_identification_code,
            description: item.description_branch,
            entity_id: item.entity_id,
            country_id: item.id_country,
            start_date: item.start_date,
            end_date: item.end_date,
          })),
        },
      ],
      token: isLoggedIn,
    };

    try {
      const leis = changedData.map((item) => item.head_office_lei);
      const validationResults = await globalOp.validateLEI(leis);
      Object.entries(validationResults).forEach(
        ([lei, result]: [string, any]) => {
          if (result.valid) {
            notification.success({
              message: lei,
              description: result.details,
              duration: 5,
            });
          } else {
            notification.error({
              message: lei,
              description: result.details,
              duration: 5,
            });
          }
        },
      );

      const allValid = Object.values(validationResults).map(
        (r: any) => r.valid === true,
      );

      if (allValid.every(Boolean)) {
        const response = await branchOp.insertBranches(formattedData);
        if (response?.status === 200) {
          setDataSource((prev) =>
            prev.map((item) => ({ ...item, isNew: false, isEdited: false })),
          );
          notification.success({
            message: 'Info',
            description: 'The data was successfully submitted.',
            placement: 'topRight',
            duration: 20,
          });
        }
      } else {
        console.warn('One or more LEIs are invalid');
      }
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };
  const columns = getDefaultColumns(
    handleDateChange,
    handleSave,
    handleAddRow,
    handleRemoveRow,
    handleInactivate,
    countries,
    t,
  );

  return (
    dataSource && (
      <>
        <Styled.HighlightedTable>
          <CustomTable
            columns={columns}
            dataSource={dataSource}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
            locale={
              query.entity
                ? {
                    emptyText: (
                      <Button type="primary" onClick={() => handleAddRow()}>
                        {t('GENERAL.ADD_FIRST_ROW')}
                      </Button>
                    ),
                  }
                : undefined
            }
          />
        </Styled.HighlightedTable>

        <Styled.ButtonRow>
          {dataSource.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
          {dataSource.length > 0 && (
            <SaveButton
              token={token}
              hasChanges={dataSource.some((i) => i.isNew || i.isEdited)}
              onClick={handleSubmit}
            />
          )}
        </Styled.ButtonRow>
      </>
    )
  );
};

export default Branch;
